import { collection, query, where, getDocs, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';

/**
 * جلب المراجعات المعتمدة لولي الأمر
 * @param {string} educationLevel - المرحلة التعليمية ('middle' أو 'high')
 * @param {number} grade - الصف (1, 2, 3)
 * @param {string} type - نوع المراجعة ('solvedReview' أو 'unsolvedReview')
 * @returns {Promise<Array>} قائمة المراجعات
 */
export async function getParentReviews(educationLevel, grade, type) {
  try {
    const reviewsRef = collection(db, 'exam_reviews');
    
    // تحويل educationLevel من 'middle'/'high' إلى القيمة المحفوظة في Firestore
    // في Flutter يتم حفظها كـ 'middle' أو 'high' (بدون EducationLevel.)
    const educationLevelValue = educationLevel === 'middle' ? 'middle' : 'high';
    
    // تحويل type من 'solvedReview'/'unsolvedReview' إلى القيمة المحفوظة
    const typeValue = type === 'solvedReview' ? 'solvedReview' : 'unsolvedReview';
    
    // تحويل status
    const statusValue = 'sentToParent';
    
    let q = query(
      reviewsRef,
      where('educationLevel', '==', educationLevelValue),
      where('grade', '==', grade),
      where('status', '==', statusValue)
    );
    
  if (type) {
    q = query(q, where('type', '==', typeValue));
  }
  
  // استخدام sentToParentAt للترتيب لأنه يتم حفظه عند إرسال الملف لولي الأمر
  // هذا يعطي ترتيب صحيح حسب ترتيب الرفع
  let snapshot;
  try {
    q = query(q, orderBy('sentToParentAt', 'asc'));
    snapshot = await getDocs(q);
  } catch (error) {
    // إذا فشل (مثل عدم وجود index)، نجرب بدون orderBy
    snapshot = await getDocs(q);
  }
    
    // Logging فقط في وضع التطوير
    if (process.env.NODE_ENV === 'development') {
      console.log(`[ReviewsService] جلب المراجعات: المرحلة=${educationLevelValue}, الصف=${grade}, النوع=${typeValue}`);
      console.log(`[ReviewsService] عدد المراجعات: ${snapshot.docs.length}`);
    }
    
    // ترتيب البيانات حسب id (Firebase IDs مرتبة زمنياً حسب وقت الإنشاء)
    // Firebase IDs تحتوي على timestamp في البداية ومرتبة زمنياً
    // هذا يضمن الترتيب الصحيح حسب ترتيب الرفع حتى لو كانت التواريخ متساوية
    const sortedDocs = [...snapshot.docs].sort((a, b) => {
      // استخدام id مباشرة - Firebase IDs مرتبة زمنياً
      // لكن localeCompare يعطي ترتيب أبجدي، لذا نستخدم مقارنة مباشرة
      const idA = a.id || '';
      const idB = b.id || '';
      
      // Firebase IDs مرتبة زمنياً في الواقع
      // لكن localeCompare لا يحافظ على الترتيب الزمني
      // الحل: استخدام مقارنة مباشرة للـ IDs
      // Firebase IDs عادة تبدأ بـ timestamp في base36
      
      // محاولة استخراج timestamp من Firebase ID (أول 8 أحرف)
      // Firebase IDs تبدأ بـ timestamp في base36
      try {
        const timestampA = parseInt(idA.substring(0, 8), 36) || 0;
        const timestampB = parseInt(idB.substring(0, 8), 36) || 0;
        
        if (timestampA !== 0 && timestampB !== 0 && timestampA !== timestampB) {
          return timestampA - timestampB;
        }
      } catch (e) {
        // إذا فشل استخراج timestamp، نستخدم مقارنة مباشرة
      }
      
      // Fallback: استخدام sentToParentAt إذا كان مختلفاً
      const dataA = a.data();
      const dataB = b.data();
      const sentA = dataA.sentToParentAt?.toDate()?.getTime() || 0;
      const sentB = dataB.sentToParentAt?.toDate()?.getTime() || 0;
      
      if (sentA !== 0 && sentB !== 0 && sentA !== sentB) {
        return sentA - sentB;
      }
      
      // Fallback نهائي: استخدام createdAt
      const createdA = dataA.createdAt?.toDate()?.getTime() || 0;
      const createdB = dataB.createdAt?.toDate()?.getTime() || 0;
      
      if (createdA !== createdB) {
        return createdA - createdB;
      }
      
      // إذا فشل كل شيء، استخدم مقارنة مباشرة للـ IDs
      // Firebase IDs مرتبة زمنياً في الواقع
      return idA < idB ? -1 : idA > idB ? 1 : 0;
    });
    
    const reviews = sortedDocs.map((doc, index) => {
      const data = doc.data();
      
      // تنظيف رابط الملف من escape characters وتحويل Unicode escape sequences
      let fileUrl = data.fileUrl || '';
      if (fileUrl) {
        fileUrl = String(fileUrl)
          .replace(/\\\//g, '/') // إزالة escape characters للشرطات المائلة
          // تحويل Unicode escape sequences (\u0627) إلى أحرف عربية فعلية
          .replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => {
            return String.fromCharCode(parseInt(code, 16));
          })
          .trim();
      }
      
      const review = {
        id: doc.id,
        ...data,
        fileUrl: fileUrl, // استخدام الرابط النظيف
        uploadedAt: data.uploadedAt?.toDate() || new Date(),
        reviewedAt: data.reviewedAt?.toDate() || null,
        sentToParentAt: data.sentToParentAt?.toDate() || null,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        _firebaseIndex: index, // حفظ الفهرس الأصلي من Firebase
      };
      // Logging فقط في وضع التطوير
      if (process.env.NODE_ENV === 'development') {
        console.log(`[ReviewsService] مراجعة: ${review.subject} - ${review.fileName}`);
        console.log(`[ReviewsService] رابط الملف الأصلي: ${review.fileUrl}`);
      }
      return review;
    });
    
    return reviews;
  } catch (error) {
    console.error('خطأ في جلب المراجعات:', error);
    throw error;
  }
}

/**
 * جلب المواد الفريدة من المراجعات
 * @param {Array} reviews - قائمة المراجعات
 * @returns {Array} قائمة المواد الفريدة
 */
export function getUniqueSubjects(reviews) {
  const subjects = new Set();
  reviews.forEach(review => {
    if (review.subject) {
      subjects.add(review.subject);
    }
  });
  return Array.from(subjects).sort();
}

/**
 * جلب جدول الاختبارات لولي الأمر (one-time read)
 * @param {string} educationLevel - المرحلة التعليمية ('middle' أو 'high')
 * @param {number} grade - الصف (1, 2, 3)
 * @returns {Promise<Object|null>} جدول الاختبارات أو null إذا لم يوجد
 */
export async function getTestSchedule(educationLevel, grade) {
  try {
    const reviewsRef = collection(db, 'exam_reviews');
    
    // تحويل educationLevel من 'middle'/'high' إلى القيمة المحفوظة في Firestore
    const educationLevelValue = educationLevel === 'middle' ? 'middle' : 'high';
    
    // البحث عن جدول الاختبارات
    let q = query(
      reviewsRef,
      where('educationLevel', '==', educationLevelValue),
      where('grade', '==', grade),
      where('type', '==', 'testSchedule'),
      where('status', '==', 'sentToParent')
    );
    
    let snapshot;
    try {
      // محاولة مع orderBy
      q = query(q, orderBy('uploadedAt', 'desc'));
      snapshot = await getDocs(q);
    } catch (orderByError) {
      // إذا فشل orderBy (مثل عدم وجود index)، نجرب بدون orderBy
      q = query(
        reviewsRef,
        where('educationLevel', '==', educationLevelValue),
        where('grade', '==', grade),
        where('type', '==', 'testSchedule'),
        where('status', '==', 'sentToParent')
      );
      snapshot = await getDocs(q);
    }
    
    if (snapshot.empty) {
      return null;
    }
    
    // أخذ أحدث جدول اختبارات
    const doc = snapshot.docs[0];
    const data = doc.data();
    
    // تنظيف رابط الملف من escape characters وتحويل Unicode escape sequences
    let fileUrl = data.fileUrl || '';
    if (fileUrl) {
      fileUrl = String(fileUrl)
        .replace(/\\\//g, '/')
        .replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => {
          return String.fromCharCode(parseInt(code, 16));
        })
        .trim();
    }
    
    const testSchedule = {
      id: doc.id,
      fileName: data.fileName || 'جدول الاختبارات.pdf',
      fileUrl: fileUrl,
      fileType: data.fileType || 'pdf',
      uploadedAt: data.uploadedAt?.toDate() || new Date(),
      subject: data.subject || 'جدول الاختبارات',
    };
    
    return testSchedule;
  } catch (error) {
    console.error('خطأ في جلب جدول الاختبارات:', error);
    throw error;
  }
}

/**
 * الاستماع لتحديثات جدول الاختبارات في الوقت الفعلي
 * @param {string} educationLevel - المرحلة التعليمية ('middle' أو 'high')
 * @param {number} grade - الصف (1, 2, 3)
 * @param {function} callback - دالة يتم استدعاؤها عند التحديث (testSchedule|null)
 * @returns {function} دالة لإلغاء الاشتراك
 */
export function subscribeToTestSchedule(educationLevel, grade, callback) {
  const reviewsRef = collection(db, 'exam_reviews');
  const educationLevelValue = educationLevel === 'middle' ? 'middle' : 'high';
  
  let q = query(
    reviewsRef,
    where('educationLevel', '==', educationLevelValue),
    where('grade', '==', grade),
    where('type', '==', 'testSchedule'),
    where('status', '==', 'sentToParent')
  );
  
  // محاولة إضافة orderBy
  try {
    q = query(q, orderBy('uploadedAt', 'desc'));
  } catch (error) {
    // إذا فشل orderBy، نستخدم query بدون orderBy
    q = query(
      reviewsRef,
      where('educationLevel', '==', educationLevelValue),
      where('grade', '==', grade),
      where('type', '==', 'testSchedule'),
      where('status', '==', 'sentToParent')
    );
  }
  
  const unsubscribe = onSnapshot(q, (snapshot) => {
    // التحقق من أن البيانات من الخادم وليست من cache
    const source = snapshot.metadata.fromCache ? 'cache' : 'server';
    if (process.env.NODE_ENV === 'development') {
      console.log(`[TestScheduleService] تحديث: ${snapshot.docs.length} مستندات، المصدر: ${source}`);
    }
    
    // التحقق من التغييرات (للحذف والإضافة)
    const changes = snapshot.docChanges();
    if (changes && changes.length > 0) {
      changes.forEach((change) => {
        if (process.env.NODE_ENV === 'development') {
          if (change.type === 'removed') {
            console.log('[TestScheduleService] ✅ تم حذف جدول اختبار:', change.doc.id);
          } else if (change.type === 'added') {
            console.log('[TestScheduleService] ✅ تم إضافة جدول اختبار جديد:', change.doc.id);
          } else if (change.type === 'modified') {
            console.log('[TestScheduleService] ✅ تم تعديل جدول اختبار:', change.doc.id);
          }
        }
      });
    } else if (process.env.NODE_ENV === 'development') {
      // إذا لم تكن هناك تغييرات، قد تكون هذه أول مرة يتم تحميل البيانات
      console.log('[TestScheduleService] تحميل أولي للبيانات');
    }
    
    if (snapshot.empty) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[TestScheduleService] لا يوجد جداول اختبارات');
      }
      callback(null);
      return;
    }
    
    // أخذ أحدث جدول اختبارات
    const doc = snapshot.docs[0];
    const data = doc.data();
    
    // تنظيف رابط الملف
    let fileUrl = data.fileUrl || '';
    if (fileUrl) {
      fileUrl = String(fileUrl)
        .replace(/\\\//g, '/')
        .replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => {
          return String.fromCharCode(parseInt(code, 16));
        })
        .trim();
    }
    
    const testSchedule = {
      id: doc.id,
      fileName: data.fileName || 'جدول الاختبارات.pdf',
      fileUrl: fileUrl,
      fileType: data.fileType || 'pdf',
      uploadedAt: data.uploadedAt?.toDate() || new Date(),
      subject: data.subject || 'جدول الاختبارات',
    };
    
    callback(testSchedule);
  }, (error) => {
    // معالجة أفضل للأخطاء
    if (error.code === 'unavailable') {
      console.warn('[TestScheduleService] Firestore غير متاح - يعمل في وضع offline');
      callback(null);
    } else if (error.code === 'permission-denied') {
      console.error('[TestScheduleService] خطأ في الصلاحيات:', error);
      callback(null);
    } else {
      console.error('[TestScheduleService] خطأ في الاستماع لتحديثات جدول الاختبارات:', error);
      callback(null);
    }
  });
  
  return unsubscribe;
}

/**
 * الاستماع لتحديثات المراجعات في الوقت الفعلي
 * @param {string} educationLevel - المرحلة التعليمية ('middle' أو 'high')
 * @param {number} grade - الصف (1, 2, 3)
 * @param {string} type - نوع المراجعة ('solvedReview' أو 'unsolvedReview')
 * @param {function} callback - دالة يتم استدعاؤها عند التحديث (reviews[])
 * @returns {function} دالة لإلغاء الاشتراك
 */
export function subscribeToParentReviews(educationLevel, grade, type, callback) {
  const reviewsRef = collection(db, 'exam_reviews');
  const educationLevelValue = educationLevel === 'middle' ? 'middle' : 'high';
  const typeValue = type === 'solvedReview' ? 'solvedReview' : 'unsolvedReview';
  const statusValue = 'sentToParent';
  
  let q = query(
    reviewsRef,
    where('educationLevel', '==', educationLevelValue),
    where('grade', '==', grade),
    where('status', '==', statusValue)
  );
  
  if (type) {
    q = query(q, where('type', '==', typeValue));
  }
  
  // استخدام sentToParentAt للترتيب لأنه يتم حفظه عند إرسال الملف لولي الأمر
  // هذا يعطي ترتيب صحيح حسب ترتيب الرفع
  try {
    q = query(q, orderBy('sentToParentAt', 'asc'));
  } catch (error) {
    // إذا فشل (مثل عدم وجود index)، نستخدم query بدون orderBy
  }
  
  const unsubscribe = onSnapshot(q, 
    (snapshot) => {
      // التحقق من أن البيانات من الخادم وليست من cache
      const source = snapshot.metadata.fromCache ? 'cache' : 'server';
      if (process.env.NODE_ENV === 'development') {
        console.log(`[ReviewsService] تحديث: ${snapshot.docs.length} مراجعة، المصدر: ${source}`);
      }
      
      // التحقق من التغييرات (للحذف والإضافة)
      const changes = snapshot.docChanges();
      if (changes && changes.length > 0) {
        changes.forEach((change) => {
          if (process.env.NODE_ENV === 'development') {
            if (change.type === 'removed') {
              console.log('[ReviewsService] ✅ تم حذف مراجعة:', change.doc.id);
            } else if (change.type === 'added') {
              console.log('[ReviewsService] ✅ تم إضافة مراجعة جديدة:', change.doc.id);
            } else if (change.type === 'modified') {
              console.log('[ReviewsService] ✅ تم تعديل مراجعة:', change.doc.id);
            }
          }
        });
      } else if (process.env.NODE_ENV === 'development') {
        // إذا لم تكن هناك تغييرات، قد تكون هذه أول مرة يتم تحميل البيانات
        console.log('[ReviewsService] تحميل أولي للبيانات');
      }
      
      // ترتيب البيانات حسب id (Firebase IDs مرتبة زمنياً حسب وقت الإنشاء)
      // Firebase IDs تحتوي على timestamp في البداية ومرتبة زمنياً
      // هذا يضمن الترتيب الصحيح حسب ترتيب الرفع حتى لو كانت التواريخ متساوية
      const sortedDocs = [...snapshot.docs].sort((a, b) => {
        // استخدام id مباشرة - Firebase IDs مرتبة زمنياً
        const idA = a.id || '';
        const idB = b.id || '';
        
        // محاولة استخراج timestamp من Firebase ID (أول 8 أحرف)
        // Firebase IDs تبدأ بـ timestamp في base36
        try {
          const timestampA = parseInt(idA.substring(0, 8), 36) || 0;
          const timestampB = parseInt(idB.substring(0, 8), 36) || 0;
          
          if (timestampA !== 0 && timestampB !== 0 && timestampA !== timestampB) {
            return timestampA - timestampB;
          }
        } catch (e) {
          // إذا فشل استخراج timestamp، نستخدم مقارنة مباشرة
        }
        
        // Fallback: استخدام sentToParentAt إذا كان مختلفاً
        const dataA = a.data();
        const dataB = b.data();
        const sentA = dataA.sentToParentAt?.toDate()?.getTime() || 0;
        const sentB = dataB.sentToParentAt?.toDate()?.getTime() || 0;
        
        if (sentA !== 0 && sentB !== 0 && sentA !== sentB) {
          return sentA - sentB;
        }
        
        // Fallback نهائي: استخدام createdAt
        const createdA = dataA.createdAt?.toDate()?.getTime() || 0;
        const createdB = dataB.createdAt?.toDate()?.getTime() || 0;
        
        if (createdA !== createdB) {
          return createdA - createdB;
        }
        
        // إذا فشل كل شيء، استخدم مقارنة مباشرة للـ IDs
        // Firebase IDs مرتبة زمنياً في الواقع
        return idA < idB ? -1 : idA > idB ? 1 : 0;
      });
      
      const reviews = sortedDocs.map((doc, index) => {
        const data = doc.data();
        
        // تنظيف رابط الملف
        let fileUrl = data.fileUrl || '';
        if (fileUrl) {
          fileUrl = String(fileUrl)
            .replace(/\\\//g, '/')
            .replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => {
              return String.fromCharCode(parseInt(code, 16));
            })
            .trim();
        }
        
        return {
          id: doc.id,
          ...data,
          fileUrl: fileUrl,
          uploadedAt: data.uploadedAt?.toDate() || new Date(),
          reviewedAt: data.reviewedAt?.toDate() || null,
          sentToParentAt: data.sentToParentAt?.toDate() || null,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          _firebaseIndex: index, // حفظ الفهرس الأصلي من Firebase
        };
      });
      
      callback(reviews);
    }, 
    (error) => {
      // معالجة أفضل للأخطاء
      if (error.code === 'unavailable') {
        console.warn('[ReviewsService] Firestore غير متاح - يعمل في وضع offline');
        // استدعاء callback مع array فارغ للإشارة إلى وجود خطأ
        callback([]);
      } else if (error.code === 'permission-denied') {
        console.error('[ReviewsService] خطأ في الصلاحيات:', error);
        callback([]);
      } else {
        console.error('[ReviewsService] خطأ في الاستماع لتحديثات المراجعات:', error);
        callback([]);
      }
    }
  );
  
  return unsubscribe;
}

